import React, { useContext, useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';
import Grid from '@mui/material/Grid';
import { AuthContext } from '../reducers/Auth/AuthContext';
import Button from '@mui/material/Button';
import { EditField } from './EditField';
import { EditYesNo } from './EditYesNo';
import BarLoader from 'react-spinners/BarLoader';
import { uuid } from '../utils/uuid';
import { PlaceStoreContext } from '../reducers/Place/PlaceContext';
import { updateService } from '../apis/updateService';
import { addService } from '../apis/addService';
import { deleteService } from '../apis/deleteService';
import AreYouSureYesNo from './AreYouSureYesNo';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { EditDetails } from './EditDetails';
import { updateServiceImage } from '../apis/updateServiceImage';
import { EditImage } from './EditImage';
import { removeImageFromBucket } from '../apis/removeImageFromBucket';
import { COLORS } from '../utils/colors';
import config from '../utils/config';
import PublishButton from './PublishButton';

const ManageService = (props) => {
  const { serviceId, onClose } = props;
  const [placeState] = useContext(PlaceStoreContext);
  const [authState] = useContext(AuthContext);
  const [file, setFile] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [serviceDelete, setServiceDelete] = useState({
    visible: false,
    isDeleting: false,
  });

  const {
    services: { serviceId__service },
  } = placeState;
  const {
    user: { placeId, role },
  } = authState;

  const isBasic = role === 'basic';

  const service = serviceId__service[serviceId];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let initialValues = null;
    let imageDetails = {
      image: null,
      local: false,
    };
    if (serviceId === 'new') {
      initialValues = {
        id: 'new',
        active: true,
        name: '',
        image: imageDetails,
        expand: false,
        description: '',
      };
    } else if (serviceId) {
      const service = serviceId__service[serviceId];
      let image = null;
      if (service.has_image) {
        image = `${config.uri.bucket}/${placeId}/high/${
          service.id
        }.jpg?time=${new Date().getTime()}`;
      }

      imageDetails = {
        image,
        local: false,
      };

      initialValues = {
        id: service.id,
        active: service.active,
        name: service.name,
        image: imageDetails,
        description: service.description || '',
        expand: service?.expand !== undefined ? service.expand : false,
      };
    }

    initialValues && setInitialData({ initialValues, imageUrl: imageDetails.image });
    if (serviceDelete.visible) {
      setServiceDelete({ visible: false, isDeleting: false });
      onClose();
    }
  }, [serviceId]);

  const handleServiceDelete = () => {
    setServiceDelete({ visible: true, isDeleting: false });
  };

  const serviceOnNo = () => {
    setServiceDelete({ visible: false, isDeleting: false });
  };

  const serviceOnYes = () => {
    setServiceDelete({ visible: true, isDeleting: true });
    deleteService(placeId, serviceId);
  };

  if (initialData === null || !serviceId) return null;

  if (serviceDelete.isDeleting) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: `calc(90vw - ${Math.round((window.innerWidth * 8) / 100)}px)`,
          height: '80vh',
          margin: '0 auto',
        }}
      >
        <Grid container spacing={2} style={{ padding: '16px' }}>
          <Grid item xs={12}>
            <AreYouSureYesNo
              onNo={serviceOnNo}
              onYes={serviceOnYes}
              loading={serviceDelete.isDeleting}
              loadingMessage="Deleting service ..."
              warning="This service will be deleted with all the categories and products!"
            />
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <Grid container style={{ marginTop: '40px' }}>
      <Grid item xs={12}>
        <Formik
          enableReinitialize
          initialValues={initialData.initialValues}
          validationSchema={Yup.object({
            active: Yup.boolean().required('Required'),
            name: Yup.string()
              .min(3, 'Must have at least 3 chars')
              .max(64, 'Must be 64 characters or less')
              .required('Required'),
            description: Yup.string().max(2048, 'Must be 2048 characters or less'),
            expand: Yup.boolean().required('Required'),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            if (!placeId) return;
            const newService = service.id === 'new';
            setSubmitting(true);

            let hasImage = false;

            const selectedServiceId = newService ? uuid() : service.id;
            if (file) {
              hasImage = true;
              console.log('Updating Image');
              await updateServiceImage(placeId, selectedServiceId, file);
              console.log('Done updating image');
            }

            if (service.has_image && removeImage && !newService) {
              console.log('Removing image from service');
              await removeImageFromBucket(placeId, 'service', service.id);
              console.log('Done removing image from service');
            }

            if (newService) {
              console.log('Adding New Service');
              values.hasImage = hasImage;
              await addService(placeId, selectedServiceId, values);
              console.log('Done adding new service');
            } else {
              console.log('Updating service');
              await updateService(placeId, selectedServiceId, values);
              console.log('Done updating service');
            }

            if (file) {
              setFile(null);
            }
            setSubmitting(false);
            onClose();
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => {
            if (isSubmitting)
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: `calc(100vw - ${Math.round((window.innerWidth * 8) / 100)}px)`,
                    height: '80vh',
                    margin: '0 auto',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <p style={{ margin: 0, paddingBottom: '20px', fontWeight: 'bold' }}>
                      SAVING CHANGES
                    </p>
                    <BarLoader height={4} width={100} color="#000000" loading={true} />
                  </div>
                </div>
              );
            const submitDisabled = isSubmitting || _.isEqual(initialData.initialValues, values);
            const title =
              serviceId === 'new'
                ? `NEW SERVICE`
                : serviceId__service[serviceId].name.toUpperCase();

            return (
              <div>
                <Form encType="multipart/form-data">
                  <Grid container spacing={2} justify="center" style={{ maxWidth: '500px' }}>
                    <Grid
                      item
                      xs={12}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          padding: 0,
                          fontWeight: 'bold',
                          color: COLORS.INFO,
                        }}
                      >
                        {title}
                      </p>
                      <PublishButton
                        name="PUBLISH"
                        disabled={!removeImage === false ? false : submitDisabled}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <EditYesNo label="Active" name="active" />
                    </Grid>
                    <Grid item xs={12}>
                      <EditField label="Name (64 max characters)" name="name" />
                    </Grid>
                    {!isBasic && <Grid item xs={12}>
                      <EditImage
                        initialImage={initialData.imageUrl}
                        image={values.image}
                        removeImage={removeImage}
                        setRemoveImage={setRemoveImage}
                        setFieldValue={setFieldValue}
                        setFile={setFile}
                        file={file}
                        height="185px"
                      />
                    </Grid>}
                    <Grid item xs={12}>
                      <p className="f08">Expand will not work if the menu is spreaded below the services display!</p>
                    </Grid>
                    <Grid item xs={12}>
                      <EditYesNo label="Expand" name="expand" />
                    </Grid>
                    <Grid item xs={12}>
                      <EditDetails label="Description (2048 max characters)" name="description" />
                    </Grid>
                    {serviceId !== 'new' && (
                      <Grid item xs={12} style={{ marginTop: 16 }}>
                        {!serviceDelete.visible ? (
                          <Button variant="contained" color="error" onClick={handleServiceDelete}>
                            <span>DELETE</span>
                            <DeleteForeverIcon className="s-24-i txt-white ml-16" />
                          </Button>
                        ) : (
                          <div className="ml-16">
                            <AreYouSureYesNo
                              onNo={serviceOnNo}
                              onYes={serviceOnYes}
                              loading={serviceDelete.isDeleting}
                              loadingMessage="Deleting service ..."
                              warning="This service will be deleted with all the categories and products!"
                            />
                          </div>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Form>
                <div style={{ height: '200px' }} />
              </div>
            );
          }}
        </Formik>
      </Grid>
    </Grid>
  );
};

export default ManageService;
